import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Settings, Save, Loader2, Power, PowerOff, Shield, UserPlus, 
  Gift, Users, Trash2, Edit, Crown, ChevronDown, ChevronUp,
  MessageSquare, Mic, Volume2, Check, X
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SystemSettingsPanel = ({ token }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [specialUsers, setSpecialUsers] = useState([]);
  const [tutorConfig, setTutorConfig] = useState(null);
  const [expandedSection, setExpandedSection] = useState('payments');
  
  // New admin form
  const [newAdmin, setNewAdmin] = useState({ email: '', role: 'admin', notes: '' });
  
  // New special user form
  const [newSpecialUser, setNewSpecialUser] = useState({
    email: '',
    user_type: 'gift',
    credits: 1000,
    subscription_tier: 'premium',
    notes: '',
    expires_at: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [configRes, adminsRes, specialRes, tutorRes] = await Promise.all([
        axios.get(`${API}/system/config`, { headers }),
        axios.get(`${API}/system/admins`, { headers }),
        axios.get(`${API}/system/special-users`, { headers }),
        axios.get(`${API}/system/tutor-ai/config`, { headers })
      ]);
      
      setConfig(configRes.data);
      setAdmins(adminsRes.data.admins || []);
      setSpecialUsers(specialRes.data.special_users || []);
      setTutorConfig(tutorRes.data);
    } catch (error) {
      toast.error('Error cargando configuración del sistema');
    } finally {
      setLoading(false);
    }
  };

  const togglePayments = async () => {
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const newState = !config.payments_enabled;
      
      await axios.put(`${API}/system/payments/toggle?enabled=${newState}`, {}, { headers });
      
      setConfig(prev => ({ ...prev, payments_enabled: newState }));
      toast.success(newState ? '💰 Pagos ACTIVADOS' : '🔒 Pagos DESACTIVADOS');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error cambiando estado');
    } finally {
      setSaving(false);
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/system/config`, config, { headers });
      toast.success('Configuración guardada');
    } catch (error) {
      toast.error('Error guardando configuración');
    } finally {
      setSaving(false);
    }
  };

  const addAdmin = async () => {
    if (!newAdmin.email) {
      toast.error('Email requerido');
      return;
    }
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/system/admins`, newAdmin, { headers });
      toast.success(`Administrador ${newAdmin.email} creado`);
      setNewAdmin({ email: '', role: 'admin', notes: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error creando admin');
    }
  };

  const removeAdmin = async (email) => {
    if (!window.confirm(`¿Eliminar admin ${email}?`)) return;
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/system/admins/${email}`, { headers });
      toast.success('Admin eliminado');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error eliminando admin');
    }
  };

  const addSpecialUser = async () => {
    if (!newSpecialUser.email) {
      toast.error('Email requerido');
      return;
    }
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const data = {
        ...newSpecialUser,
        credits: newSpecialUser.credits === '' ? null : parseInt(newSpecialUser.credits),
        expires_at: newSpecialUser.expires_at || null
      };
      
      await axios.post(`${API}/system/special-users`, data, { headers });
      toast.success(`Usuario especial ${newSpecialUser.email} configurado`);
      setNewSpecialUser({
        email: '',
        user_type: 'gift',
        credits: 1000,
        subscription_tier: 'premium',
        notes: '',
        expires_at: ''
      });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error creando usuario especial');
    }
  };

  const removeSpecialUser = async (email) => {
    if (!window.confirm(`¿Revocar acceso especial para ${email}?`)) return;
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/system/special-users/${email}`, { headers });
      toast.success('Acceso especial revocado');
      loadData();
    } catch (error) {
      toast.error('Error revocando acceso');
    }
  };

  const saveTutorConfig = async () => {
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/system/tutor-ai/config`, tutorConfig, { headers });
      toast.success('Configuración del Tutor IA guardada');
    } catch (error) {
      toast.error('Error guardando configuración');
    } finally {
      setSaving(false);
    }
  };

  const toggleTutorPlan = (planId) => {
    setTutorConfig(prev => {
      const plans = prev.available_plans || [];
      if (plans.includes(planId)) {
        return { ...prev, available_plans: plans.filter(p => p !== planId) };
      } else {
        return { ...prev, available_plans: [...plans, planId] };
      }
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Section: Payments ON/OFF */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <button
          onClick={() => setExpandedSection(expandedSection === 'payments' ? null : 'payments')}
          className="w-full p-4 flex items-center justify-between hover:bg-slate-50"
        >
          <div className="flex items-center gap-3">
            {config?.payments_enabled ? (
              <Power className="w-6 h-6 text-emerald-500" />
            ) : (
              <PowerOff className="w-6 h-6 text-slate-400" />
            )}
            <div className="text-left">
              <h3 className="font-semibold text-slate-800">Sistema de Pagos</h3>
              <p className="text-sm text-slate-500">
                {config?.payments_enabled ? 'ACTIVADO - Los usuarios deben pagar' : 'DESACTIVADO - Modo pruebas'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
              config?.payments_enabled 
                ? 'bg-emerald-100 text-emerald-700' 
                : 'bg-slate-100 text-slate-600'
            }`}>
              {config?.payments_enabled ? 'ON' : 'OFF'}
            </span>
            {expandedSection === 'payments' ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </div>
        </button>
        
        {expandedSection === 'payments' && (
          <div className="p-4 border-t border-slate-200 bg-slate-50">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-sm text-slate-600">
                  {config?.payments_enabled 
                    ? '⚠️ Los pagos están activados. Los usuarios nuevos deberán seleccionar y pagar un plan.'
                    : '✅ Modo pruebas activo. Los usuarios pueden usar la plataforma sin restricciones de pago.'}
                </p>
              </div>
              <Button
                onClick={togglePayments}
                disabled={saving}
                className={config?.payments_enabled 
                  ? 'bg-rose-600 hover:bg-rose-700' 
                  : 'bg-emerald-600 hover:bg-emerald-700'}
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {config?.payments_enabled ? 'Desactivar Pagos' : 'Activar Pagos'}
              </Button>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
              <div>
                <label className="text-xs text-slate-500">Días de prueba</label>
                <Input
                  type="number"
                  value={config?.trial_days || 7}
                  onChange={(e) => setConfig({ ...config, trial_days: parseInt(e.target.value) })}
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-xs text-slate-500">Créditos trial</label>
                <Input
                  type="number"
                  value={config?.default_trial_credits || 100}
                  onChange={(e) => setConfig({ ...config, default_trial_credits: parseInt(e.target.value) })}
                  className="mt-1"
                />
              </div>
              <div className="flex items-end gap-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={config?.require_plan_selection || false}
                    onChange={(e) => setConfig({ ...config, require_plan_selection: e.target.checked })}
                    className="rounded"
                  />
                  <span className="text-sm">Selección de plan obligatoria</span>
                </label>
              </div>
              <div className="flex items-end">
                <Button onClick={saveConfig} disabled={saving} variant="outline" className="w-full">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Guardar
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Section: Administrators */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <button
          onClick={() => setExpandedSection(expandedSection === 'admins' ? null : 'admins')}
          className="w-full p-4 flex items-center justify-between hover:bg-slate-50"
        >
          <div className="flex items-center gap-3">
            <Shield className="w-6 h-6 text-indigo-500" />
            <div className="text-left">
              <h3 className="font-semibold text-slate-800">Administradores</h3>
              <p className="text-sm text-slate-500">{admins.length} administradores configurados</p>
            </div>
          </div>
          {expandedSection === 'admins' ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </button>
        
        {expandedSection === 'admins' && (
          <div className="p-4 border-t border-slate-200 bg-slate-50">
            {/* Admins list */}
            <div className="space-y-2 mb-4 max-h-48 overflow-y-auto">
              {admins.map((admin, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-white rounded-lg border">
                  <div className="flex items-center gap-3">
                    {admin.role === 'super_admin' ? (
                      <Crown className="w-5 h-5 text-amber-500" />
                    ) : (
                      <Shield className="w-5 h-5 text-indigo-500" />
                    )}
                    <div>
                      <p className="font-medium text-slate-800">{admin.email}</p>
                      <p className="text-xs text-slate-500">{admin.role} {admin.is_hardcoded && '(Sistema)'}</p>
                    </div>
                  </div>
                  {!admin.is_hardcoded && (
                    <Button variant="ghost" size="sm" onClick={() => removeAdmin(admin.email)}>
                      <Trash2 className="w-4 h-4 text-rose-500" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
            
            {/* Add admin form */}
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <label className="text-xs text-slate-500">Email del nuevo admin</label>
                <Input
                  value={newAdmin.email}
                  onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })}
                  placeholder="admin@ejemplo.com"
                />
              </div>
              <select
                value={newAdmin.role}
                onChange={(e) => setNewAdmin({ ...newAdmin, role: e.target.value })}
                className="px-3 py-2 border border-slate-300 rounded-md text-sm"
              >
                <option value="admin">Admin</option>
                <option value="super_admin">Super Admin</option>
              </select>
              <Button onClick={addAdmin}>
                <UserPlus className="w-4 h-4 mr-2" /> Añadir
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Section: Special Users (Gifts, Testers) */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <button
          onClick={() => setExpandedSection(expandedSection === 'special' ? null : 'special')}
          className="w-full p-4 flex items-center justify-between hover:bg-slate-50"
        >
          <div className="flex items-center gap-3">
            <Gift className="w-6 h-6 text-pink-500" />
            <div className="text-left">
              <h3 className="font-semibold text-slate-800">Usuarios Especiales</h3>
              <p className="text-sm text-slate-500">Regalos, testers, partners ({specialUsers.length})</p>
            </div>
          </div>
          {expandedSection === 'special' ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </button>
        
        {expandedSection === 'special' && (
          <div className="p-4 border-t border-slate-200 bg-slate-50">
            {/* Special users list */}
            <div className="space-y-2 mb-4 max-h-60 overflow-y-auto">
              {specialUsers.map((user, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-white rounded-lg border">
                  <div className="flex items-center gap-3">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      user.user_type === 'gift' ? 'bg-pink-100 text-pink-700' :
                      user.user_type === 'tester' ? 'bg-blue-100 text-blue-700' :
                      'bg-purple-100 text-purple-700'
                    }`}>
                      {user.user_type}
                    </span>
                    <div>
                      <p className="font-medium text-slate-800">{user.email}</p>
                      <p className="text-xs text-slate-500">
                        {user.credits === null ? '∞ créditos' : `${user.credits} créditos`} • {user.subscription_tier}
                        {user.notes && ` • ${user.notes}`}
                      </p>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => removeSpecialUser(user.email)}>
                    <Trash2 className="w-4 h-4 text-rose-500" />
                  </Button>
                </div>
              ))}
              {specialUsers.length === 0 && (
                <p className="text-sm text-slate-500 text-center py-4">No hay usuarios especiales configurados</p>
              )}
            </div>
            
            {/* Add special user form */}
            <div className="bg-white p-4 rounded-lg border space-y-3">
              <h4 className="font-medium text-slate-800">Añadir usuario especial</h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <div>
                  <label className="text-xs text-slate-500">Email*</label>
                  <Input
                    value={newSpecialUser.email}
                    onChange={(e) => setNewSpecialUser({ ...newSpecialUser, email: e.target.value })}
                    placeholder="amigo@ejemplo.com"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-500">Tipo</label>
                  <select
                    value={newSpecialUser.user_type}
                    onChange={(e) => setNewSpecialUser({ ...newSpecialUser, user_type: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                  >
                    <option value="gift">Regalo</option>
                    <option value="tester">Tester</option>
                    <option value="partner">Partner</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-slate-500">Créditos (vacío = ∞)</label>
                  <Input
                    type="number"
                    value={newSpecialUser.credits}
                    onChange={(e) => setNewSpecialUser({ ...newSpecialUser, credits: e.target.value })}
                    placeholder="1000"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-500">Plan</label>
                  <select
                    value={newSpecialUser.subscription_tier}
                    onChange={(e) => setNewSpecialUser({ ...newSpecialUser, subscription_tier: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                  >
                    <option value="top">Top</option>
                    <option value="premium">Premium</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-slate-500">Expira (opcional)</label>
                  <Input
                    type="date"
                    value={newSpecialUser.expires_at}
                    onChange={(e) => setNewSpecialUser({ ...newSpecialUser, expires_at: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-500">Notas</label>
                  <Input
                    value={newSpecialUser.notes}
                    onChange={(e) => setNewSpecialUser({ ...newSpecialUser, notes: e.target.value })}
                    placeholder="Amigo enfermero"
                  />
                </div>
              </div>
              <Button onClick={addSpecialUser} className="bg-pink-600 hover:bg-pink-700">
                <Gift className="w-4 h-4 mr-2" /> Crear Usuario Especial
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Section: Tutor IA Configuration */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <button
          onClick={() => setExpandedSection(expandedSection === 'tutor' ? null : 'tutor')}
          className="w-full p-4 flex items-center justify-between hover:bg-slate-50"
        >
          <div className="flex items-center gap-3">
            <MessageSquare className="w-6 h-6 text-purple-500" />
            <div className="text-left">
              <h3 className="font-semibold text-slate-800">Tutor IA con Voz</h3>
              <p className="text-sm text-slate-500">Configuración de acceso y voz (ElevenLabs)</p>
            </div>
          </div>
          {expandedSection === 'tutor' ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </button>
        
        {expandedSection === 'tutor' && tutorConfig && (
          <div className="p-4 border-t border-slate-200 bg-slate-50 space-y-4">
            {/* Plans with access */}
            <div>
              <h4 className="text-sm font-medium text-slate-700 mb-2">Planes con acceso al Tutor IA:</h4>
              <div className="flex gap-2 flex-wrap">
                {['trial', 'top', 'premium'].map(planId => (
                  <button
                    key={planId}
                    onClick={() => toggleTutorPlan(planId)}
                    className={`px-3 py-2 rounded-lg border flex items-center gap-2 ${
                      tutorConfig.available_plans?.includes(planId)
                        ? 'bg-purple-100 border-purple-300 text-purple-700'
                        : 'bg-white border-slate-200 text-slate-500'
                    }`}
                  >
                    {tutorConfig.available_plans?.includes(planId) ? (
                      <Check className="w-4 h-4" />
                    ) : (
                      <X className="w-4 h-4" />
                    )}
                    {planId.charAt(0).toUpperCase() + planId.slice(1)}
                  </button>
                ))}
              </div>
            </div>
            
            {/* Voice settings */}
            <div className="grid grid-cols-2 gap-4">
              <label className="flex items-center gap-3 p-3 bg-white rounded-lg border cursor-pointer">
                <input
                  type="checkbox"
                  checked={tutorConfig.voice_enabled || false}
                  onChange={(e) => setTutorConfig({ ...tutorConfig, voice_enabled: e.target.checked })}
                  className="rounded"
                />
                <Volume2 className="w-5 h-5 text-amber-500" />
                <div>
                  <p className="font-medium text-slate-800">Voz del Tutor</p>
                  <p className="text-xs text-slate-500">ElevenLabs TTS</p>
                </div>
              </label>
              
              <label className="flex items-center gap-3 p-3 bg-white rounded-lg border cursor-pointer">
                <input
                  type="checkbox"
                  checked={tutorConfig.voice_input_enabled || false}
                  onChange={(e) => setTutorConfig({ ...tutorConfig, voice_input_enabled: e.target.checked })}
                  className="rounded"
                />
                <Mic className="w-5 h-5 text-blue-500" />
                <div>
                  <p className="font-medium text-slate-800">Entrada por Voz</p>
                  <p className="text-xs text-slate-500">Speech-to-Text</p>
                </div>
              </label>
            </div>
            
            {/* ElevenLabs Agent ID */}
            <div>
              <label className="text-sm text-slate-600">ElevenLabs Agent ID (opcional)</label>
              <Input
                value={tutorConfig.elevenlabs_agent_id || ''}
                onChange={(e) => setTutorConfig({ ...tutorConfig, elevenlabs_agent_id: e.target.value })}
                placeholder="agent_xxxxxxxxxxxxx"
                className="mt-1"
              />
              <p className="text-xs text-slate-500 mt-1">ID del agente conversacional de ElevenLabs</p>
            </div>
            
            <Button onClick={saveTutorConfig} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Guardar configuración Tutor IA
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SystemSettingsPanel;
